/**
 * CountrySelect Component
 *
 * Thin wrapper over @elsapiens/ui's CountrySelect. It feeds the elSDK component
 * the full country list from our geolocation service (via `customCountries`),
 * so we get elSDK's flags, dial codes and keyboard navigation while keeping our
 * server-sourced data. This replaces the previous local searchable-select fork,
 * which lacked arrow-key navigation.
 */

import { useState, useEffect, forwardRef } from 'react'

import { CountrySelect as SdkCountrySelect, type Country as SdkCountry } from '@elsapiens/ui'

import { getCountries } from '../../services/geolocation-service'
import { firstNonBlank } from '../../utils/formatters'

export interface CountrySelectProps {
  /** Selected country code (e.g., "IN", "US") */
  value?: string
  /** Callback when country changes — (code, name) */
  onChange?: (countryCode: string | null, countryName: string | null) => void
  /** Placeholder text */
  placeholder?: string
  /** Whether the field is disabled */
  disabled?: boolean
  /** Error message */
  error?: string
  /** Helper text shown below the control */
  helperText?: string
  /** Custom class name */
  className?: string
  /** ID for accessibility / label association */
  id?: string
  /** Hide error and helper messages */
  hideMessage?: boolean
}

// ============================================================================
// Hook — load countries from the geolocation service in elSDK's Country shape
// ============================================================================

function useGeolocationCountries(): SdkCountry[] {
  const [countries, setCountries] = useState<SdkCountry[]>([])

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const data = await getCountries()
        // Note: no dialCode — elSDK's CountrySelect turns a dialCode into an
        // option `description`, which forces taller (56px) dropdown rows. This
        // is an address picker, so we keep rows compact (40px) with just the flag.
        const mapped: SdkCountry[] = data
          .map((c) => ({
            code: c.identifier,
            name: c.name,
            image: c.image,
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
        setCountries(mapped)
      } catch {
        // silently ignore — falls back to an empty list
      }
    }
    void load()
  }, [])

  return countries
}

// ============================================================================
// Component
// ============================================================================

/**
 * Searchable country dropdown backed by the geolocation service.
 *
 * @example
 * <CountrySelect value={country} onChange={(code) => setCountry(code)} />
 */
export const CountrySelect = forwardRef<HTMLInputElement, CountrySelectProps>(({
  value,
  onChange,
  placeholder = 'Select country',
  disabled,
  error,
  helperText,
  className,
  id,
  hideMessage,
}, ref) => {
  const countries = useGeolocationCountries()

  const handleChange = (code: string): void => {
    const name = countries.find((c) => c.code === code)?.name ?? null
    onChange?.(code || null, name)
  }

  return (
    <div id={id}>
      <SdkCountrySelect
        ref={ref}
        className={className}
        customCountries={countries}
        disabled={disabled}
        error={error}
        hideMessage={hideMessage}
        placeholder={placeholder}
        value={firstNonBlank(value)}
        onChange={handleChange}
      />
      {helperText && !error && (
        <p className="text-xs text-muted-foreground el-mt-xs">{helperText}</p>
      )}
    </div>
  )
})
CountrySelect.displayName = 'CountrySelect'

export default CountrySelect
